import { sniffAvif, sniffHeic } from "./sniff-heic";
import { convertHeicToJpeg } from "./convert-heic";

/**
 * Thrown when an image is a recognised-but-unsupported format (currently AVIF).
 * Callers surface `message` to the user instead of a generic "damaged file".
 */
export class UnsupportedImageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnsupportedImageError";
  }
}

/**
 * If `file`'s header bytes are HEIC/HEIF, convert to a JPEG `File` (via the
 * worker); otherwise return `file` unchanged. Throws `UnsupportedImageError`
 * for AVIF (not supported yet) and re-throws if HEIC conversion fails.
 */
export async function ensureRenderable(file: File): Promise<File> {
  const header = await file.slice(0, 32).arrayBuffer();
  // AVIF shares HEIC's ISO-BMFF container and generic `mif1` brand but can't go
  // through the HEIC decoder. Reject it up front (rather than letting it fail
  // opaquely inside the worker) until we support it.
  if (sniffAvif(header)) {
    throw new UnsupportedImageError(
      "AVIF images aren't supported yet — please use JPEG, PNG, or HEIC.",
    );
  }
  if (!sniffHeic(header)) return file;
  const jpeg = await convertHeicToJpeg(file);
  const base = file.name.replace(/\.(heic|heif)$/i, "");
  const name = `${base || "image"}.jpg`;
  return new File([jpeg], name, { type: "image/jpeg", lastModified: file.lastModified });
}
